import { IApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import type { IConfigLogSettings } from '../interface/config';
import { LOG_LEVEL, ILogger } from './log.if';

const _levelToApplicationInsightsLevel: Record<LOG_LEVEL, SeverityLevel> = {
    [LOG_LEVEL.MILESTONE]: SeverityLevel.Information,
    [LOG_LEVEL.TRACE]: SeverityLevel.Verbose,
    [LOG_LEVEL.VERBOSE]: SeverityLevel.Verbose,
    [LOG_LEVEL.INFO]: SeverityLevel.Information,
    [LOG_LEVEL.WARN]: SeverityLevel.Warning,
    [LOG_LEVEL.ERROR]: SeverityLevel.Error,
    [LOG_LEVEL.FATAL]: SeverityLevel.Critical,
    [LOG_LEVEL.ASSERT]: SeverityLevel.Critical,
};

function _applicationInsightsLog(
    logLevel: LOG_LEVEL,
    logger: IApplicationInsights | null,
    message: string,
) {
    if (!logger) {
        return;
    }

    switch (logLevel) {
        case LOG_LEVEL.MILESTONE:
        case LOG_LEVEL.VERBOSE:
        case LOG_LEVEL.TRACE:
        case LOG_LEVEL.INFO:
        case LOG_LEVEL.WARN:
            logger.trackTrace({
                message,
                severityLevel: _levelToApplicationInsightsLevel[logLevel],
            });
            break;

        default:
        case LOG_LEVEL.ERROR:
        case LOG_LEVEL.FATAL:
        case LOG_LEVEL.ASSERT:
            logger.trackException({
                exception: new Error(message),
                severityLevel: _levelToApplicationInsightsLevel[logLevel],
            });
            break;
    }
}

function _setupAILogger(
    levels: Array<LOG_LEVEL>,
    settings: IConfigLogSettings,
): Promise<IApplicationInsights> {
    return new Promise(async function (resolve, reject) {
        try {
            const debugLogEnabled =
                levels.includes(LOG_LEVEL.MILESTONE) ||
                levels.includes(LOG_LEVEL.VERBOSE) ||
                levels.includes(LOG_LEVEL.INFO);
            const warnLogEnabled = levels.includes(LOG_LEVEL.WARN);
            const loggingLevel =
                1 + (debugLogEnabled || warnLogEnabled ? 1 : 0); /* 1=Critical, 2=Warn+Others */
            const aiPackage = await import('@microsoft/applicationinsights-web');

            const logger = new aiPackage.ApplicationInsights({
                config: {
                    instrumentationKey: settings.authorizationKey,
                    enableAutoRouteTracking: true,
                    loggingLevelTelemetry: loggingLevel,
                    loggingLevelConsole: loggingLevel,
                    namePrefix: `${settings.name ?? 'ai-logger'}-`,
                    ...settings,
                },
            });

            logger.loadAppInsights();

            resolve(logger);
        } catch (error) {
            reject(error);
        }
    });
}

function logger(levels: Array<LOG_LEVEL>, settings: IConfigLogSettings): ILogger {
    let remoteAILogger: IApplicationInsights | null = null;

    _setupAILogger(levels, settings)
        .then(logger => (remoteAILogger = logger))
        .catch(error => console.error(`Failed to get AI logger: ${error}`));

    const logger: ILogger = {
        assert: (condition, msg) =>
            condition && _applicationInsightsLog(LOG_LEVEL.ASSERT, remoteAILogger, msg),
        log: (level, msg) => _applicationInsightsLog(level, remoteAILogger, msg),
    };

    return logger;
}

export default logger;
